// FILE: client/src/components/ChatPane.js

import { useEffect, useRef, useState } from "react";

export default function ChatPane({
  socket,
  room,
  isHost,
  hostId,
  mutedUsers,
  muteUser,
  unmuteUser,
  chatMessages,
  chatInput,
  setChatInput,
  sendChat,
  chatEndRef,
  isSelfMuted,
  requestUnmute,
  unmuteStatusText,
}) {
  const chatScrollRef = useRef(null);
  const shouldStickToBottomRef = useRef(true);
  const didInitScrollRef = useRef(false);
  const prevMessageCountRef = useRef(0);
  const composerRef = useRef(null);
  const [keyboardOpen, setKeyboardOpen] = useState(false);

  useEffect(() => {
    const el = chatScrollRef.current;
    if (!el) return;

    const updateStickiness = () => {
      const distanceFromBottom =
        el.scrollHeight - el.scrollTop - el.clientHeight;

      shouldStickToBottomRef.current = distanceFromBottom <= 80;
    };

    updateStickiness();
    el.addEventListener("scroll", updateStickiness);

    return () => {
      el.removeEventListener("scroll", updateStickiness);
    };
  }, []);

  useEffect(() => {
    const el = chatScrollRef.current;
    if (!el) return;

    const messageCount = Array.isArray(chatMessages) ? chatMessages.length : 0;
    const lastMessage = messageCount ? chatMessages[messageCount - 1] : null;
    const lastMessageIsSelf = !!lastMessage && lastMessage.id === socket?.id;

    if (!didInitScrollRef.current) {
      el.scrollTop = el.scrollHeight;
      shouldStickToBottomRef.current = true;
      didInitScrollRef.current = true;
      prevMessageCountRef.current = messageCount;
      return;
    }

    const hasNewMessage = messageCount > prevMessageCountRef.current;

    if (hasNewMessage && (shouldStickToBottomRef.current || lastMessageIsSelf)) {
      el.scrollTop = el.scrollHeight;
      shouldStickToBottomRef.current = true;
    }

    prevMessageCountRef.current = messageCount;
  }, [chatMessages, socket?.id]);

  useEffect(() => {
    const vv = window.visualViewport;
    if (!vv) return;

    const composer = composerRef.current;

    const updateKeyboard = () => {
      const keyboardHeight =
        window.innerHeight - vv.height - vv.offsetTop;

      const open = keyboardHeight > 120;

      setKeyboardOpen(open);

      document.body.classList.toggle("keyboard-open", open);

      if (composer) {
        composer.style.transform = open
          ? `translateY(-${keyboardHeight}px)`
          : "translateY(0)";
      }
    };

    vv.addEventListener("resize", updateKeyboard);
    vv.addEventListener("scroll", updateKeyboard);

    updateKeyboard();

    return () => {
      vv.removeEventListener("resize", updateKeyboard);
      vv.removeEventListener("scroll", updateKeyboard);

      document.body.classList.remove("keyboard-open");

      if (composer) {
        composer.style.transform = "translateY(0)";
      }
    };
  }, []);

  return (
    <div className="card-pad chatBox pane">
      <div className="chatScroll" ref={chatScrollRef}>
        {chatMessages.map((m, idx) => {
          const isSelf = m.id === socket?.id;

          return (
            <div
              key={`${m.ts}-${idx}`}
              className={`chatMsgRow ${isSelf ? "right" : "left"}`}
              style={{
                display: "flex",
                justifyContent: isSelf ? "flex-end" : "flex-start",
                marginBottom: "8px",
              }}
            >
              <div
                className={`chatMsg ${isSelf ? "own right" : "other left"}`}
                style={{ maxWidth: "75%" }}
              >
                <div className="chatText">{m.text}</div>
              </div>
            </div>
          );
        })}
        <div ref={chatEndRef} />
      </div>

      {!isHost && isSelfMuted && (
        <div
          style={{
            marginTop: "8px",
            marginBottom: "6px",
            fontSize: "14px",
            fontWeight: 700,
            opacity: 0.95,
          }}
        >
          You are muted
          {unmuteStatusText && (
            <div
              style={{
                marginTop: "4px",
                fontSize: "12px",
                fontWeight: 500,
                opacity: 0.8,
              }}
            >
              {unmuteStatusText}
            </div>
          )}
        </div>
      )}

      <div
        className={`chatComposer ${keyboardOpen ? "keyboardMode" : ""}`}
        ref={composerRef}
      >
        <input
          className="input"
          value={chatInput}
          onChange={(e) => setChatInput(e.target.value)}
          placeholder={isSelfMuted ? "Muted…" : "Type a message…"}
          disabled={isSelfMuted}
          onKeyDown={(e) => {
            if (e.key === "Enter") sendChat();
          }}
        />

        <button
          className="btn btn-primary"
          onClick={sendChat}
          disabled={isSelfMuted}
        >
          Send
        </button>

        {!isHost && isSelfMuted && (
          <button
            className="btn btn-primary"
            onClick={requestUnmute}
          >
            Request Unmute
          </button>
        )}
      </div>
    </div>
  );
}